/**
 * Main JavaScript file for Sravia Agency Theme
 */

(function() {
    'use strict';
    
    // Initialize GSAP when available
    function initGSAP() {
        if (typeof gsap !== 'undefined') {
            gsap.registerPlugin(ScrollTrigger);
            
            // Register ScrollSmoother for enhanced smooth scrolling
            if (typeof ScrollSmoother !== 'undefined') {
                gsap.registerPlugin(ScrollSmoother);
                console.log('GSAP initialized with ScrollTrigger and ScrollSmoother');
            } else {
                console.log('GSAP initialized with ScrollTrigger (ScrollSmoother not available)');
            }
        }
    }
    
    // Initialize case studies card animations
    function initCaseStudiesCardAnimations() {
        if (typeof gsap === 'undefined' || typeof ScrollTrigger === 'undefined') {
            console.warn('GSAP or ScrollTrigger not available - case studies animations disabled');
            return;
        }
        
        // This function is now empty as sample-wrapper cards have been removed
        // Keeping the function for potential future use
        console.log('Case studies card animations function called - no sample-wrapper cards to animate');
    }
    
    // DOM Ready
    document.addEventListener('DOMContentLoaded', function() {
        // Initialize scroll optimizations first
        optimizeScrollPerformance();
        
        initGSAP();
        initCaseStudiesCardAnimations();
        initMobileMenu();
        initAnimations();
        initSectionTitleDots();
        initSmoothScrolling();
        initHeaderScroll();
        initCreativeWorkCarousel(); // Add carousel initialization
        initPortfolioLightbox(); // Add portfolio lightbox initialization
        initCaseStudiesAnimations(); // Add case studies animations
        initCaseStudiesCarousel(); // Add case studies carousel animations
        initCaseStudyTitleAnimations(); // Add case study title animations
        initCaseStudySectionRevealAnimations(); // Add case study section reveal animations
        initOriginStoryAnimations(); // Add origin story animations
        initCompanyEvolutionAnimations(); // Add company evolution animations
        initLeadershipAnimations(); // Add leadership section animations
        initTeamSectionWordAnimation(); // Add team section word animation
        initPortfolioAnimations(); // Add portfolio animations
        initCTAAnimations(); // Add CTA section animations
        
        
    });

    /**
     * Initialize Mobile Menu
     */
    function initMobileMenu() {
        const mobileMenuToggle = document.getElementById('mobile-menu-toggle');
        const mobileMenu = document.getElementById('mobile-menu');
        
        if (!mobileMenuToggle || !mobileMenu) return;

        mobileMenuToggle.addEventListener('click', function() {
            const isExpanded = this.getAttribute('aria-expanded') === 'true';
            
            this.setAttribute('aria-expanded', !isExpanded);
            mobileMenu.classList.toggle('hidden');
            
            // Toggle icon
            const icons = this.querySelectorAll('svg');
            icons.forEach(icon => icon.classList.toggle('hidden'));
        });

        // Close mobile menu when clicking outside
        document.addEventListener('click', function(event) {
            if (!mobileMenuToggle.contains(event.target) && !mobileMenu.contains(event.target)) {
                mobileMenuToggle.setAttribute('aria-expanded', 'false');
                mobileMenu.classList.add('hidden');
                
                const icons = mobileMenuToggle.querySelectorAll('svg');
                icons.forEach((icon, index) => {
                    icon.classList.toggle('hidden', index === 1);
                });
            }
        });
    }

    /**
     * Initialize Section Title Dots Positioning and Animation
     */
    function initSectionTitleDots() {
        const sectionTitles = document.querySelectorAll('.section-title');
        
        sectionTitles.forEach((sectionTitle, index) => {
            const titleElement = sectionTitle.querySelector('.section-title-main h2');
            const dotsContainer = sectionTitle.querySelector('.section-title-dots');
            const dotsSvg = sectionTitle.querySelector('.animate-circular-dots');
            
            if (titleElement && dotsContainer) {
                // Position dots after a small delay to ensure fonts are loaded
                setTimeout(() => {
                    positionDotsBehindFirstLetter(titleElement, dotsContainer);
                    
                    // Ensure animation starts
                    if (dotsSvg) {
                        dotsSvg.style.animationPlayState = 'running';
                        
                        // Add intersection observer to pause animation when not visible
                        const dotsObserver = new IntersectionObserver((entries) => {
                            entries.forEach(entry => {
                                if (entry.isIntersecting) {
                                    entry.target.style.animationPlayState = 'running';
                                } else {
                                    entry.target.style.animationPlayState = 'paused';
                                }
                            });
                        }, { threshold: 0.1 });
                        
                        dotsObserver.observe(dotsSvg);
                    }
                }, 100 + (index * 50)); // Stagger the positioning
            }
        });
    }

    /**
     * Position circular dots behind the first letter of the title
     */
    function positionDotsBehindFirstLetter(titleElement, dotsContainer) {
        const titleText = titleElement.textContent.trim();
        if (!titleText) return;
        
        const firstLetter = titleText.charAt(0);
        
        // Create a temporary span to measure the first letter
        const tempSpan = document.createElement('span');
        tempSpan.textContent = firstLetter;
        tempSpan.style.position = 'absolute';
        tempSpan.style.visibility = 'hidden';
        tempSpan.style.whiteSpace = 'nowrap';
        tempSpan.style.font = window.getComputedStyle(titleElement).font;
        tempSpan.style.fontWeight = window.getComputedStyle(titleElement).fontWeight;
        tempSpan.style.fontSize = window.getComputedStyle(titleElement).fontSize;
        tempSpan.style.lineHeight = window.getComputedStyle(titleElement).lineHeight;
        tempSpan.style.fontFamily = window.getComputedStyle(titleElement).fontFamily;
        
        document.body.appendChild(tempSpan);
        
        const firstLetterRect = tempSpan.getBoundingClientRect();
        document.body.removeChild(tempSpan);
        
        // Get the title element's position relative to its container
        const titleRect = titleElement.getBoundingClientRect();
        const containerRect = titleElement.closest('.section-title').getBoundingClientRect();
        
        // Calculate the first letter's position relative to the container
        const firstLetterLeft = titleRect.left - containerRect.left;
        const firstLetterTop = titleRect.top - containerRect.top;
        
        // Calculate the center position of the first letter
        const firstLetterCenterX = firstLetterLeft + (firstLetterRect.width / 2);
        const firstLetterCenterY = firstLetterTop + (firstLetterRect.height / 2);
        
        // Position the dots container so its center aligns with the first letter's center
        dotsContainer.style.position = 'absolute';
        dotsContainer.style.left = `${firstLetterCenterX - 70}px`; // 70 is half of SVG width (141/2)
        dotsContainer.style.top = `-50%`; // 70 is half of SVG height (141/2)
        dotsContainer.style.width = '141px';
        dotsContainer.style.height = '141px';
        dotsContainer.style.zIndex = '-1';
        
        // Add a class to indicate positioning is complete
        dotsContainer.classList.add('dots-positioned');
    }

    /**
     * Initialize Intersection Observer Animations
     */
    function initAnimations() {
        // Check if animations are enabled
        const animationsEnabled = document.documentElement.classList.contains('animations-enabled') || true;
        
        if (!animationsEnabled) return;

        const observerOptions = {
            threshold: 0.1,
            rootMargin: '0px 0px -50px 0px'
        };

        const observer = new IntersectionObserver(function(entries) {
            entries.forEach(entry => {
                if (entry.isIntersecting) {
                    // Add the 'animated' class to trigger the animation
                    entry.target.classList.add('animated');
                    
                    // For staggered animations, add delay
                    if (entry.target.classList.contains('award-item') || 
                        entry.target.classList.contains('portfolio-item') || 
                        entry.target.classList.contains('brand-item') || 
                        entry.target.classList.contains('case-study-item')) {
                        
                        const index = Array.from(entry.target.parentNode.children).indexOf(entry.target);
                        entry.target.style.transitionDelay = `${index * 100}ms`;
                    }
                    
                    observer.unobserve(entry.target);
                }
            });
        }, observerOptions);

        // Observe all elements with animation classes
        const animatedElements = document.querySelectorAll('.animate-fade-in-up, .animate-fade-in-down, .animate-fade-in-left, .animate-fade-in-right');
        
        animatedElements.forEach(el => {
            observer.observe(el);
        });

        // Special handling for staggered animations
        const staggeredElements = document.querySelectorAll('.award-item, .portfolio-item, .brand-item, .case-study-item');
        
        staggeredElements.forEach((el, index) => {
            // Set initial state
            el.style.transitionDelay = `${index * 100}ms`;
            observer.observe(el);
        });
    }


    /**
     * Initialize Enhanced Smooth Scrolling
     */
    function initSmoothScrolling() {
        // Handle anchor links
        const links = document.querySelectorAll('a[href^="#"]');
        
        links.forEach(link => {
            link.addEventListener('click', function(e) {
                const href = this.getAttribute('href');
                
                if (href === '#') return;
                
                const target = document.querySelector(href);
                if (target) {
                    e.preventDefault();
                    smoothScrollToElement(target);
                }
            });
        });
        
        // Initialize scroll to top button
        initScrollToTopButton();
        
        // Handle form submissions with smooth scroll
        initFormSmoothScroll();
        
        // Handle pagination with smooth scroll
        initPaginationSmoothScroll();
    }
    
    /**
     * Smooth scroll to a specific element
     */
    function smoothScrollToElement(element, offset = 0) {
        if (!element) return;
        
        const header = document.querySelector('.site-header');
        const headerHeight = header ? header.offsetHeight : 0;
        const targetPosition = element.offsetTop - headerHeight - offset;
        
        // Use native smooth scrolling if available
        if ('scrollBehavior' in document.documentElement.style) {
            window.scrollTo({
                top: targetPosition,
                behavior: 'smooth'
            });
        } else {
            // Fallback for older browsers
            smoothScrollFallback(targetPosition);
        }
    }
    
    /**
     * Fallback smooth scroll for older browsers
     */
    function smoothScrollFallback(targetPosition) {
        const startPosition = window.pageYOffset;
        const distance = targetPosition - startPosition;
        const duration = Math.min(Math.abs(distance) / 2, 1000); // Max 1 second
        let start = null;
        
        function animation(currentTime) {
            if (start === null) start = currentTime;
            const timeElapsed = currentTime - start;
            const run = easeInOutCubic(timeElapsed, startPosition, distance, duration);
            window.scrollTo(0, run);
            if (timeElapsed < duration) requestAnimationFrame(animation);
        }
        
        requestAnimationFrame(animation);
    }
    
    /**
     * Easing function for smooth scroll fallback
     */
    function easeInOutCubic(t, b, c, d) {
        t /= d / 2;
        if (t < 1) return c / 2 * t * t * t + b;
        t -= 2;
        return c / 2 * (t * t * t + 2) + b;
    }
    
    /**
     * Initialize scroll to top button
     */
    function initScrollToTopButton() {
        // Create scroll to top button if it doesn't exist
        let scrollToTopBtn = document.getElementById('scroll-to-top');
        if (!scrollToTopBtn) {
            scrollToTopBtn = document.createElement('button');
            scrollToTopBtn.id = 'scroll-to-top';
            scrollToTopBtn.className = 'scroll-to-top bg-sravia-red text-white p-3 rounded-full shadow-lg hover:bg-red-700 transition-all duration-300';
            scrollToTopBtn.innerHTML = `
                <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 10l7-7m0 0l7 7m-7-7v18"/>
                </svg>
            `;
            scrollToTopBtn.setAttribute('aria-label', 'Scroll to top');
            document.body.appendChild(scrollToTopBtn);
        }
        
        // Show/hide button based on scroll position
        let ticking = false;
        function updateScrollToTopButton() {
            const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
            const windowHeight = window.innerHeight;
            
            if (scrollTop > windowHeight) {
                scrollToTopBtn.classList.add('visible');
            } else {
                scrollToTopBtn.classList.remove('visible');
            }
            ticking = false;
        }
        
        window.addEventListener('scroll', function() {
            if (!ticking) {
                requestAnimationFrame(updateScrollToTopButton);
                ticking = true;
            }
        }, { passive: true });
        
        // Handle click to scroll to top
        scrollToTopBtn.addEventListener('click', function() {
            window.scrollTo({
                top: 0,
                behavior: 'smooth'
            });
        });
    }

    
    /**
     * Initialize smooth scroll for form submissions
     */
    function initFormSmoothScroll() {
        const forms = document.querySelectorAll('form');
        
        forms.forEach(form => {
            form.addEventListener('submit', function(e) {
                // Add a small delay to allow form processing
                setTimeout(() => {
                    // Scroll to form or success message
                    const formRect = form.getBoundingClientRect();
                    if (formRect.top < 0 || formRect.bottom > window.innerHeight) {
                        smoothScrollToElement(form, 100);
                    }
                }, 100);
            });
        });
    }

    
    /**
     * Initialize smooth scroll for pagination
     */
    function initPaginationSmoothScroll() {
        const paginationLinks = document.querySelectorAll('.pagination a, .page-numbers a');
        
        paginationLinks.forEach(link => {
            link.addEventListener('click', function(e) {
                // Add loading state
                document.body.style.opacity = '0.8';
                document.body.style.pointerEvents = 'none';
                
                // Scroll to top after a short delay
                setTimeout(() => {
                    window.scrollTo({
                        top: 0,
                        behavior: 'smooth'
                    });
                }, 100);
            });
        });
    }


    /**
     * Initialize Header Scroll Effects
     */
    function initHeaderScroll() {
        const header = document.querySelector('.site-header');
        if (!header) return;

        let lastScrollTop = 0;
        const scrollThreshold = 100;
        let ticking = false;

        function updateHeader() {
            const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
            
            if (scrollTop > scrollThreshold) {
                header.classList.add('scrolled');
            } else {
                header.classList.remove('scrolled');
            }

            // Hide/show header on scroll
            if (scrollTop > lastScrollTop && scrollTop > scrollThreshold) {
                header.classList.add('header-hidden');
            } else {
                header.classList.remove('header-hidden');
            }

            lastScrollTop = scrollTop;
            ticking = false;
        }

        window.addEventListener('scroll', function() {
            if (!ticking) {
                requestAnimationFrame(updateHeader);
                ticking = true;
            }
        }, { passive: true });
    }

    /**
     * Utility function to debounce scroll events
     */
    function debounce(func, wait) {
        let timeout;
        return function executedFunction(...args) {
            const later = () => {
                clearTimeout(timeout);
                func(...args);
            };
            clearTimeout(timeout);
            timeout = setTimeout(later, wait);
        };
    }

    /**
     * Throttle function for scroll events
     */
    function throttle(func, limit) {
        let inThrottle;
        return function() {
            const args = arguments;
            const context = this;
            if (!inThrottle) {
                func.apply(context, args);
                inThrottle = true;
                setTimeout(() => inThrottle = false, limit);
            }
        };
    }

    /**
     * Smooth scroll helpers (link fade, popstate). We intentionally do not patch
     * EventTarget.addEventListener or inject duplicate global * { ... } rules: that
     * interfered with GSAP ScrollTrigger/Observer wheel handling and could make the
     * document only respond to scrollbar dragging (no wheel scrolling over content).
     * Base styles live in src/css/tailwind.css @layer base.
     */
    function optimizeScrollPerformance() {
        initPageNavigationSmoothScroll();
    }
    
    /**
     * Initialize smooth scrolling for page navigation
     */
    function initPageNavigationSmoothScroll() {
        // Handle internal page links
        const internalLinks = document.querySelectorAll('a[href^="/"], a[href^="./"], a[href^="../"]');
        
        internalLinks.forEach(link => {
            link.addEventListener('click', function(e) {
                const href = this.getAttribute('href');
                
                // Only handle internal links that don't have target="_blank"
                if (this.target === '_blank' || this.target === '_new') return;
                
                // Check if it's a same-page link
                if (href.startsWith('#') || href === window.location.pathname) return;
                
                // Add smooth transition effect
                document.body.style.transition = 'opacity 0.3s ease-out';
                document.body.style.opacity = '0.8';
                
                // Reset opacity after navigation
                setTimeout(() => {
                    document.body.style.opacity = '1';
                    document.body.style.transition = '';
                }, 100);
            });
        });
        
        // Handle browser back/forward navigation
        window.addEventListener('popstate', function() {
            // Smooth scroll to top when navigating back/forward
            setTimeout(() => {
                window.scrollTo({
                    top: 0,
                    behavior: 'smooth'
                });
            }, 50);
        });
    }

    /**
     * Initialize Lazy Loading for Images
     */
    function initLazyLoading() {
        if ('IntersectionObserver' in window) {
            const imageObserver = new IntersectionObserver((entries, observer) => {
                entries.forEach(entry => {
                    if (entry.isIntersecting) {
                        const img = entry.target;
                        img.src = img.dataset.src;
                        img.classList.remove('lazy');
                        imageObserver.unobserve(img);
                    }
                });
            });

            const lazyImages = document.querySelectorAll('img[data-src]');
            lazyImages.forEach(img => imageObserver.observe(img));
        }
    }

    /**
     * Initialize Video Background Controls
     */
    function initVideoBackground() {
        const videoBackgrounds = document.querySelectorAll('.hero-section video');
        
        videoBackgrounds.forEach(video => {
            // Pause video when not in viewport to save resources
            const videoObserver = new IntersectionObserver((entries) => {
                entries.forEach(entry => {
                    if (entry.isIntersecting) {
                        video.play().catch(e => console.log('Video autoplay failed:', e));
                    } else {
                        video.pause();
                    }
                });
            }, { threshold: 0.1 });
            
            videoObserver.observe(video);
        });
    }

    /**
     * Initialize Form Handling
     */
    function initForms() {
        const forms = document.querySelectorAll('form');
        
        forms.forEach(form => {
            form.addEventListener('submit', function(e) {
                const submitButton = form.querySelector('button[type="submit"]');
                if (submitButton) {
                    submitButton.disabled = true;
                    submitButton.innerHTML = 'Sending...';
                }
            });
        });
    }

    /**
     * Initialize Search Functionality
     */
    function initSearch() {
        const searchForm = document.querySelector('.search-form');
        if (!searchForm) return;

        searchForm.addEventListener('submit', function(e) {
            const searchInput = this.querySelector('input[name="s"]');
            if (!searchInput.value.trim()) {
                e.preventDefault();
                searchInput.focus();
            }
        });
    }

    /**
     * Initialize Creative Work Carousel
     */
    function initCreativeWorkCarousel() {
        const carouselSection = document.getElementById('creative-work-carousel');
        if (!carouselSection) return;

        const carouselTrack = document.getElementById('carousel-track');
        const carouselItems = carouselTrack.querySelectorAll('.carousel-item');
        const prevBtn = carouselSection.querySelector('.carousel-prev');
        const nextBtn = carouselSection.querySelector('.carousel-next');
        const scrollDots = carouselSection.querySelectorAll('.scroll-dot');
        const progressBar = carouselSection.querySelector('.scroll-progress-bar');
        
        if (!carouselTrack || carouselItems.length === 0) return;

        const prefersReducedMotion =
            typeof window.matchMedia !== 'undefined' &&
            window.matchMedia('(prefers-reduced-motion: reduce)').matches;

        const useScrollScrub =
            !prefersReducedMotion &&
            typeof gsap !== 'undefined' &&
            typeof ScrollTrigger !== 'undefined';

        let scrubTween = null;

        let currentIndex = 0;
        let isDragging = false;
        let startX = 0;
        let currentX = 0;
        let dragOffset = 0;

        const getViewportEl = () =>
            carouselSection.querySelector('.carousel-container') || carouselSection;

        const getItemWidth = () => carouselItems[0].offsetWidth + 32; // 32px for margins
        const getTotalWidth = () => getItemWidth() * carouselItems.length;
        const getMaxScroll = () =>
            Math.max(0, getTotalWidth() - getViewportEl().clientWidth);

        /** Horizontal travel for scroll-scrub (actual track overflow). */
        const getMaxTranslateX = () =>
            Math.max(0, carouselTrack.scrollWidth - getViewportEl().clientWidth);

        const setTransform = (x) => {
            carouselTrack.style.transform = `translateX(${x}px)`;
        };

        const updateScrollDots = () => {
            scrollDots.forEach((dot, index) => {
                dot.classList.toggle('active', index === currentIndex);
            });
        };

        const updateNavigationButtons = () => {
            if (prevBtn) {
                prevBtn.disabled = currentIndex === 0;
                prevBtn.style.opacity = currentIndex === 0 ? '0.5' : '1';
            }
            if (nextBtn) {
                nextBtn.disabled = currentIndex === carouselItems.length - 1;
                nextBtn.style.opacity =
                    currentIndex === carouselItems.length - 1 ? '0.5' : '1';
            }
        };

        const syncDotsFromTranslateX = (translateXAbs) => {
            const itemWidth = getItemWidth();
            const newIndex = Math.min(
                carouselItems.length - 1,
                Math.max(0, Math.round(translateXAbs / itemWidth))
            );
            if (newIndex !== currentIndex) {
                currentIndex = newIndex;
                updateScrollDots();
                updateNavigationButtons();
            }
        };

        const moveToIndex = (index) => {
            if (index < 0 || index >= carouselItems.length) return;

            if (useScrollScrub) {
                if (scrubTween && scrubTween.scrollTrigger) {
                    const st = scrubTween.scrollTrigger;
                    const maxI = carouselItems.length - 1;
                    const progress = maxI ? index / maxI : 0;
                    const y = st.start + (st.end - st.start) * progress;
                    window.scrollTo({ top: y, behavior: 'smooth' });
                }
                return;
            }

            currentIndex = index;
            const translateX = -index * getItemWidth();

            if (typeof gsap !== 'undefined') {
                gsap.to(carouselTrack, {
                    x: translateX,
                    duration: 0.6,
                    ease: 'power2.out'
                });
            } else {
                carouselTrack.style.transition = 'transform 0.6s ease-out';
                setTransform(translateX);
                setTimeout(() => {
                    carouselTrack.style.transition = '';
                }, 600);
            }

            updateScrollDots();
            updateNavigationButtons();

            if (progressBar && carouselItems.length > 1) {
                progressBar.style.width = `${(index / (carouselItems.length - 1)) * 100}%`;
            }
        };

        const navigateRelative = (direction) => {
            moveToIndex(currentIndex + direction);
        };

        const getCurrentTransform = () => {
            const transform = carouselTrack.style.transform;
            if (!transform || transform === 'none') return 0;
            const match = transform.match(/translateX\(([^)]+)px\)/);
            return match ? parseFloat(match[1]) : 0;
        };

        const handleTouchStart = (e) => {
            startX = e.touches[0].clientX;
            isDragging = true;
            dragOffset = 0;
        };

        const handleTouchMove = (e) => {
            if (!isDragging) return;
            e.preventDefault();

            currentX = e.touches[0].clientX;
            dragOffset = currentX - startX;

            const currentTransform = getCurrentTransform();
            setTransform(currentTransform + dragOffset);
        };

        const handleTouchEnd = () => {
            if (!isDragging) return;

            const threshold = getItemWidth() * 0.3;
            if (Math.abs(dragOffset) > threshold) {
                if (dragOffset > 0 && currentIndex > 0) {
                    moveToIndex(currentIndex - 1);
                } else if (dragOffset < 0 && currentIndex < carouselItems.length - 1) {
                    moveToIndex(currentIndex + 1);
                } else {
                    moveToIndex(currentIndex);
                }
            } else {
                moveToIndex(currentIndex);
            }

            isDragging = false;
            dragOffset = 0;
        };

        const handleMouseDown = (e) => {
            startX = e.clientX;
            isDragging = true;
            dragOffset = 0;
            carouselTrack.style.cursor = 'grabbing';
        };

        const handleMouseMove = (e) => {
            if (!isDragging) return;

            currentX = e.clientX;
            dragOffset = currentX - startX;

            const currentTransform = getCurrentTransform();
            setTransform(currentTransform + dragOffset);
        };

        const handleMouseUp = () => {
            if (!isDragging) return;

            const threshold = getItemWidth() * 0.3;
            if (Math.abs(dragOffset) > threshold) {
                if (dragOffset > 0 && currentIndex > 0) {
                    moveToIndex(currentIndex - 1);
                } else if (dragOffset < 0 && currentIndex < carouselItems.length - 1) {
                    moveToIndex(currentIndex + 1);
                } else {
                    moveToIndex(currentIndex);
                }
            } else {
                moveToIndex(currentIndex);
            }

            isDragging = false;
            dragOffset = 0;
            carouselTrack.style.cursor = 'grab';
        };

        const onKeydown = (e) => {
            if (!carouselSection.contains(document.activeElement)) return;

            switch (e.key) {
                case 'ArrowLeft':
                    e.preventDefault();
                    if (currentIndex > 0) navigateRelative(-1);
                    break;
                case 'ArrowRight':
                    e.preventDefault();
                    if (currentIndex < carouselItems.length - 1) navigateRelative(1);
                    break;
                case 'Home':
                    e.preventDefault();
                    moveToIndex(0);
                    break;
                case 'End':
                    e.preventDefault();
                    moveToIndex(carouselItems.length - 1);
                    break;
            }
        };

        const initCarousel = () => {
            if (getMaxScroll() <= 0) {
                const minWidth = getItemWidth() * carouselItems.length + 100;
                carouselTrack.style.minWidth = `${minWidth}px`;
            }

            if (useScrollScrub) {
                gsap.registerPlugin(ScrollTrigger);

                scrubTween = gsap.to(carouselTrack, {
                    x: () => -getMaxTranslateX(),
                    ease: 'none',
                    scrollTrigger: {
                        trigger: carouselSection,
                        start: 'top bottom',
                        end: 'bottom top',
                        scrub: 1,
                        invalidateOnRefresh: true,
                        onUpdate: () => {
                            const x = parseFloat(gsap.getProperty(carouselTrack, 'x')) || 0;
                            const absX = Math.abs(x);
                            syncDotsFromTranslateX(absX);
                            if (progressBar) {
                                const maxX = getMaxTranslateX();
                                const pct = maxX ? Math.min(100, (absX / maxX) * 100) : 0;
                                progressBar.style.width = `${pct}%`;
                            }
                        }
                    }
                });

                ScrollTrigger.refresh();

                if (prevBtn) {
                    prevBtn.addEventListener('click', () => {
                        if (currentIndex > 0) navigateRelative(-1);
                    });
                }
                if (nextBtn) {
                    nextBtn.addEventListener('click', () => {
                        if (currentIndex < carouselItems.length - 1) navigateRelative(1);
                    });
                }
                scrollDots.forEach((dot, index) => {
                    dot.addEventListener('click', () => moveToIndex(index));
                });
                document.addEventListener('keydown', onKeydown);

                carouselTrack.style.cursor = 'grab';

                window.addEventListener('load', () => {
                    ScrollTrigger.refresh();
                });

                window.SraviaTheme = window.SraviaTheme || {};
                window.SraviaTheme.creativeWorkCarousel = {
                    moveToIndex,
                    currentIndex: () => currentIndex,
                    totalItems: () => carouselItems.length
                };
                return;
            }

            setTransform(0);
            updateScrollDots();
            updateNavigationButtons();
            if (progressBar) {
                progressBar.style.width = '0%';
            }

            carouselTrack.style.cursor = 'grab';

            carouselTrack.addEventListener('touchstart', handleTouchStart, { passive: true });
            carouselTrack.addEventListener('touchmove', handleTouchMove, { passive: false });
            carouselTrack.addEventListener('touchend', handleTouchEnd, { passive: true });

            carouselTrack.addEventListener('mousedown', handleMouseDown);
            carouselTrack.addEventListener('mousemove', handleMouseMove);
            carouselTrack.addEventListener('mouseup', handleMouseUp);
            carouselTrack.addEventListener('mouseleave', handleMouseUp);

            if (prevBtn) {
                prevBtn.addEventListener('click', () => {
                    if (currentIndex > 0) navigateRelative(-1);
                });
            }

            if (nextBtn) {
                nextBtn.addEventListener('click', () => {
                    if (currentIndex < carouselItems.length - 1) navigateRelative(1);
                });
            }

            scrollDots.forEach((dot, index) => {
                dot.addEventListener('click', () => moveToIndex(index));
            });

            document.addEventListener('keydown', onKeydown);

            window.SraviaTheme = window.SraviaTheme || {};
            window.SraviaTheme.creativeWorkCarousel = {
                moveToIndex,
                currentIndex: () => currentIndex,
                totalItems: () => carouselItems.length
            };
        };

        setTimeout(initCarousel, 100);

        let resizeTimeout;
        window.addEventListener('resize', () => {
            clearTimeout(resizeTimeout);
            resizeTimeout = setTimeout(() => {
                if (useScrollScrub && typeof ScrollTrigger !== 'undefined') {
                    ScrollTrigger.refresh();
                } else {
                    moveToIndex(currentIndex);
                }
            }, 250);
        });
    }

    // Initialize additional features
    initLazyLoading();
    initVideoBackground();
    initForms();
    initSearch();

    /**
     * Initialize Portfolio Lightbox
     */
    function initPortfolioLightbox() {
        const lightbox = document.getElementById('portfolio-lightbox');
        const lightboxClose = document.getElementById('lightbox-close');
        const lightboxPrev = document.getElementById('lightbox-prev');
        const lightboxNext = document.getElementById('lightbox-next');
        const lightboxMedia = document.getElementById('lightbox-media');
        const lightboxContent = document.getElementById('lightbox-content');
        const portfolioItems = document.querySelectorAll('.portfolio-item');
        
        if (!lightbox || !portfolioItems.length) return;
        
        let currentIndex = 0;
        let portfolioData = [];
        
        // Collect portfolio data
        portfolioItems.forEach((item, index) => {
            portfolioData.push({
                id: item.dataset.portfolioId,
                mediaType: item.dataset.mediaType,
                videoUrl: item.dataset.videoUrl,
                featuredImage: item.dataset.featuredImage,
                title: item.dataset.title,
                subtitle: item.dataset.subtitle,
                client: item.dataset.client,
                description: item.dataset.description,
                technologies: JSON.parse(item.dataset.technologies || '[]'),
                projectUrl: item.dataset.projectUrl,
                gallery: JSON.parse(item.dataset.gallery || '[]')
            });
        });
        
        // Open lightbox
        function openLightbox(index) {
            currentIndex = index;
            loadLightboxContent();
            lightbox.classList.remove('hidden');
            lightbox.classList.add('flex');
            document.body.style.overflow = 'hidden';
            
            // Update navigation buttons
            updateNavigationButtons();
        }
        
        // Close lightbox
        function closeLightbox() {
            lightbox.classList.add('hidden');
            lightbox.classList.remove('flex');
            document.body.style.overflow = '';
            
            // Stop any playing videos
            const videos = lightboxMedia.querySelectorAll('iframe, video');
            videos.forEach(video => {
                if (video.tagName === 'IFRAME') {
                    video.src = video.src; // Reset iframe to stop video
                } else {
                    video.pause();
                }
            });
        }
        
        // Load lightbox content
        function loadLightboxContent() {
            const item = portfolioData[currentIndex];
            if (!item) return;
            
            // Load media
            loadLightboxMedia(item);
            
            // Load content
            loadLightboxDetails(item);
        }
        
        // Load media content
        function loadLightboxMedia(item) {
            let mediaHTML = '';
            
            if (item.mediaType === 'video' && item.videoUrl) {
                // Handle YouTube video
                const videoId = extractYouTubeVideoId(item.videoUrl);
                if (videoId) {
                    mediaHTML = `
                        <div class="relative w-full" style="padding-bottom: 56.25%;">
                            <iframe 
                                src="https://www.youtube.com/embed/${videoId}?autoplay=1&rel=0&modestbranding=1" 
                                frameborder="0" 
                                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" 
                                allowfullscreen
                                class="absolute top-0 left-0 w-full h-full rounded-lg">
                            </iframe>
                        </div>
                    `;
                } else {
                    // Fallback to image if video URL is invalid
                    mediaHTML = `
                        <img src="${item.featuredImage}" 
                             alt="${item.title}" 
                             class="w-full h-auto rounded-lg shadow-lg">
                    `;
                }
            } else {
                // Show image
                mediaHTML = `
                    <img src="${item.featuredImage}" 
                         alt="${item.title}" 
                         class="w-full h-auto rounded-lg shadow-lg">
                `;
            }
            
            lightboxMedia.innerHTML = mediaHTML;
        }
        
        // Load content details
        function loadLightboxDetails(item) {
            let contentHTML = `
                <div class="space-y-6">
                    <div>
                        <h2 class="text-3xl font-bold text-gray-900 mb-2">${item.title}</h2>
                        ${item.subtitle ? `<p class="text-lg text-blue-600 font-medium mb-4">${item.subtitle}</p>` : ''}
                        ${item.client ? `<p class="text-gray-600 mb-4"><span class="font-semibold">Client:</span> ${item.client}</p>` : ''}
                    </div>
            `;
            
            if (item.description) {
                contentHTML += `
                    <div>
                        <h3 class="text-xl font-bold text-gray-900 mb-3">Project Overview</h3>
                        <div class="prose prose-gray max-w-none">
                            <p class="text-gray-600 leading-relaxed">${item.description}</p>
                        </div>
                    </div>
                `;
            }
            
            if (item.technologies && item.technologies.length > 0) {
                contentHTML += `
                    <div>
                        <h3 class="text-xl font-bold text-gray-900 mb-3">Technologies Used</h3>
                        <div class="flex flex-wrap gap-2">
                            ${item.technologies.map(tech => `
                                <span class="px-3 py-1 bg-blue-100 text-blue-800 text-sm font-medium rounded-full">
                                    ${tech.name}
                                </span>
                            `).join('')}
                        </div>
                    </div>
                `;
            }
            
            if (item.projectUrl) {
                contentHTML += `
                    <div>
                        <a href="${item.projectUrl}" 
                           target="_blank" 
                           rel="noopener noreferrer"
                           class="inline-flex items-center px-6 py-3 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-colors duration-200">
                            <svg class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"/>
                            </svg>
                            Visit Project
                        </a>
                    </div>
                `;
            }
            
            contentHTML += '</div>';
            lightboxContent.innerHTML = contentHTML;
        }
        
        // Extract YouTube video ID from URL
        function extractYouTubeVideoId(url) {
            const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
            const match = url.match(regExp);
            return (match && match[2].length === 11) ? match[2] : null;
        }
        
        // Update navigation buttons
        function updateNavigationButtons() {
            if (lightboxPrev) {
                lightboxPrev.style.display = portfolioData.length > 1 ? 'flex' : 'none';
                lightboxPrev.disabled = currentIndex === 0;
                lightboxPrev.style.opacity = currentIndex === 0 ? '0.5' : '1';
            }
            
            if (lightboxNext) {
                lightboxNext.style.display = portfolioData.length > 1 ? 'flex' : 'none';
                lightboxNext.disabled = currentIndex === portfolioData.length - 1;
                lightboxNext.style.opacity = currentIndex === portfolioData.length - 1 ? '0.5' : '1';
            }
        }
        
        // Navigate to previous item
        function navigatePrev() {
            if (currentIndex > 0) {
                currentIndex--;
                loadLightboxContent();
                updateNavigationButtons();
            }
        }
        
        // Navigate to next item
        function navigateNext() {
            if (currentIndex < portfolioData.length - 1) {
                currentIndex++;
                loadLightboxContent();
                updateNavigationButtons();
            }
        }
        
        // Event listeners
        portfolioItems.forEach((item, index) => {
            item.addEventListener('click', () => openLightbox(index));
        });
        
        if (lightboxClose) {
            lightboxClose.addEventListener('click', closeLightbox);
        }
        
        if (lightboxPrev) {
            lightboxPrev.addEventListener('click', navigatePrev);
        }
        
        if (lightboxNext) {
            lightboxNext.addEventListener('click', navigateNext);
        }
        
        // Close on backdrop click
        lightbox.addEventListener('click', (e) => {
            if (e.target === lightbox) {
                closeLightbox();
            }
        });
        
        // Keyboard navigation
        document.addEventListener('keydown', (e) => {
            if (!lightbox.classList.contains('flex')) return;
            
            switch(e.key) {
                case 'Escape':
                    closeLightbox();
                    break;
                case 'ArrowLeft':
                    e.preventDefault();
                    navigatePrev();
                    break;
                case 'ArrowRight':
                    e.preventDefault();
                    navigateNext();
                    break;
            }
        });
        
        // Expose lightbox controls globally
        window.SraviaTheme = window.SraviaTheme || {};
        window.SraviaTheme.portfolioLightbox = {
            open: openLightbox,
            close: closeLightbox,
            navigatePrev,
            navigateNext
        };
    }

    /**
     * Initialize Case Studies Carousel with 3D ScrollTrigger Animation
     */
    function initCaseStudiesCarousel() {
        const carouselContainer = document.querySelector('.case-studies-carousel-container');
        
        if (!carouselContainer) {
            console.log('Case studies carousel container not found');
            return;
        }

        if (typeof gsap === 'undefined' || typeof ScrollTrigger === 'undefined') {
            console.warn('GSAP or ScrollTrigger not available - case studies carousel animations disabled');
            return;
        }

        gsap.registerPlugin(ScrollTrigger);

        const targets = gsap.utils.toArray(".case-study-slide");

        if (targets.length === 0) {
            console.warn('No case study slides found');
            return;
        }

        // Calculate proper scroll distance - each slide gets 3/4 of viewport height
        const viewportHeight = window.innerHeight;
        const slideViewHeight = viewportHeight * 0.75; // 3/4 of viewport height
        const totalScrollDistance = targets.length * slideViewHeight;

        gsap.set(".case-studies-carousel-container", {
            autoAlpha: 1
        });

        // Set initial state for all slides
        gsap.set(targets, {
            transformOrigin: "center bottom",
            opacity: 0,
            y: 80,
            rotationX: 0,
            rotationY: 0,
            scale: 1
        });

        // Set first slide as visible initially
        gsap.set(targets[0], {
            opacity: 1,
            y: 0
        });

        // Create the main timeline
        const timeline = gsap.timeline({
            scrollTrigger: {
                pin: true,
                pinSpacing: true,
                scrub: 1.2, // Slightly slower scrub for smoother feel
                start: "top top",
                end: `+=${totalScrollDistance}`,
                trigger: ".case-studies-carousel-container",
                onUpdate: (self) => {
                    const progress = self.progress;
                    const totalSlides = targets.length;
                    
                    // Calculate current slide based on progress
                    const slideProgress = progress * totalSlides;
                    const currentSlideIndex = Math.floor(slideProgress);
                    const slideTransitionProgress = slideProgress - currentSlideIndex;
                    
                    // Hide all slides first
                    gsap.set(targets, { 
                        opacity: 0, 
                        y: 80,
                        rotationX: 0,
                        rotationY: 0,
                        scale: 1
                    });
                    
                    // Show current slide
                    if (targets[currentSlideIndex]) {
                        gsap.set(targets[currentSlideIndex], { 
                            opacity: 1, 
                            y: 0 
                        });
                    }
                    
                    // Handle transition to next slide (starts at 60% of slide view time)
                    if (slideTransitionProgress > 0.6 && currentSlideIndex < totalSlides - 1) {
                        const nextSlideIndex = currentSlideIndex + 1;
                        const transitionAmount = (slideTransitionProgress - 0.6) / 0.4; // 0 to 1 over 40% of slide
                        
                        if (targets[nextSlideIndex]) {
                            gsap.set(targets[nextSlideIndex], {
                                opacity: Math.min(transitionAmount, 1),
                                y: 80 - (transitionAmount * 80),
                                rotationX: transitionAmount * 10,
                                rotationY: transitionAmount * 2,
                                scale: 1 - (transitionAmount * 0.1)
                            });
                        }
                    }
                    
                    // Handle exit animation for current slide (starts at 60% of slide view time)
                    if (slideTransitionProgress > 0.6) {
                        const exitAmount = (slideTransitionProgress - 0.6) / 0.4;
                        if (targets[currentSlideIndex]) {
                            gsap.set(targets[currentSlideIndex], {
                                opacity: 1 - exitAmount,
                                y: -exitAmount * 40,
                                rotationX: exitAmount * 15,
                                rotationY: exitAmount * 3,
                                scale: 1 - (exitAmount * 0.15)
                            });
                        }
                    }
                },
                onRefresh: () => {
                    // Recalculate scroll distance on window resize
                    const newViewportHeight = window.innerHeight;
                    const newSlideViewHeight = newViewportHeight * 0.75;
                    const newTotalScrollDistance = targets.length * newSlideViewHeight;
                    
                    // Update the end point
                    timeline.scrollTrigger.vars.end = `+=${newTotalScrollDistance}`;
                    timeline.scrollTrigger.refresh();
                }
            }
        });
    }

    /**
     * Initialize Case Studies Animations
     */
    function initCaseStudiesAnimations() {
        // Check if we're on a case studies page
        const isCaseStudiesPage = document.body.classList.contains('post-type-case_studies') || 
                                 document.body.classList.contains('archive-case_studies') ||
                                 document.querySelector('.case-study-card') !== null;
        
        if (!isCaseStudiesPage) return;
        
        // Initialize GSAP animations if available
        if (typeof gsap !== 'undefined' && typeof ScrollTrigger !== 'undefined') {
            gsap.registerPlugin(ScrollTrigger);
            
            // Optimize ScrollTrigger performance
            ScrollTrigger.config({
                ignoreMobileResize: true,
                syncInterval: 60
            });
            
            // Animate case study cards on archive page
            const caseStudyCards = document.querySelectorAll('.case-study-card');
            if (caseStudyCards.length > 0) {
                // Batch optimize cards for performance
                gsap.set(caseStudyCards, {
                    willChange: "transform, opacity",
                    backfaceVisibility: "hidden",
                    transform: "translateZ(0)"
                });
                
                caseStudyCards.forEach((card, index) => {
                    gsap.fromTo(card, {
                        opacity: 0,
                        y: 60,
                        scale: 0.95
                    }, {
                        opacity: 1,
                        y: 0,
                        scale: 1,
                        duration: 0.8,
                        delay: index * 0.1,
                        ease: "power2.out",
                        scrollTrigger: {
                            trigger: card,
                            start: "top 85%",
                            end: "bottom 15%",
                            toggleActions: "play none none reverse",
                            refreshPriority: -1 // Lower priority for better performance
                        }
                    });
                });
            }
            
            // Animate single case study sections
            const caseStudySections = document.querySelectorAll('.animate-fade-in-up');
            if (caseStudySections.length > 0) {
                caseStudySections.forEach((section, index) => {
                    gsap.fromTo(section, {
                        opacity: 0,
                        y: 60,
                        scale: 0.95
                    }, {
                        opacity: 1,
                        y: 0,
                        scale: 1,
                        duration: 0.8,
                        delay: index * 0.2,
                        ease: "power2.out",
                        scrollTrigger: {
                            trigger: section,
                            start: "top 85%",
                            end: "bottom 15%",
                            toggleActions: "play none none reverse"
                        }
                    });
                });
            }
            
            // Animate hero section elements
            const heroTitle = document.querySelector('.hero-section h1');
            if (heroTitle) {
                gsap.fromTo(heroTitle, {
                    opacity: 0,
                    y: 50
                }, {
                    opacity: 1,
                    y: 0,
                    duration: 1,
                    ease: "power2.out",
                    scrollTrigger: {
                        trigger: heroTitle,
                        start: "top 80%",
                        end: "bottom 20%",
                        toggleActions: "play none none reverse"
                    }
                });
            }
            
            // Animate hero text
            const heroText = document.querySelector('.hero-section p');
            if (heroText) {
                gsap.fromTo(heroText, {
                    opacity: 0,
                    y: 30
                }, {
                    opacity: 1,
                    y: 0,
                    duration: 1,
                    ease: "power2.out",
                    scrollTrigger: {
                        trigger: heroText,
                        start: "top 80%",
                        end: "bottom 20%",
                        toggleActions: "play none none reverse"
                    }
                });
            }
            
            // Animate decorative elements
            const decorativeElements = document.querySelectorAll('.hero-section .w-16, .hero-section .w-2');
            if (decorativeElements.length > 0) {
                gsap.fromTo(decorativeElements, {
                    opacity: 0,
                    scaleX: 0
                }, {
                    opacity: 1,
                    scaleX: 1,
                    duration: 1.2,
                    delay: 0.5,
                    ease: "power2.out",
                    scrollTrigger: {
                        trigger: decorativeElements[0],
                        start: "top 80%",
                        end: "bottom 20%",
                        toggleActions: "play none none reverse"
                    }
                });
            }
            
            // Animate video thumbnail
            const videoThumbnail = document.querySelector('.hero-section .relative');
            if (videoThumbnail) {
                gsap.fromTo(videoThumbnail, {
                    opacity: 0,
                    scale: 0.9
                }, {
                    opacity: 1,
                    scale: 1,
                    duration: 1,
                    delay: 0.3,
                    ease: "power2.out",
                    scrollTrigger: {
                        trigger: videoThumbnail,
                        start: "top 80%",
                        end: "bottom 20%",
                        toggleActions: "play none none reverse"
                    }
                });
            }
        }
        
        // Fallback animations using Intersection Observer
        if (!window.gsap) {
            const observerOptions = {
                threshold: 0.1,
                rootMargin: '0px 0px -50px 0px'
            };
            
            const observer = new IntersectionObserver((entries) => {
                entries.forEach(entry => {
                    if (entry.isIntersecting) {
                        entry.target.classList.add('animated');
                        observer.unobserve(entry.target);
                    }
                });
            }, observerOptions);
            
            const animatedElements = document.querySelectorAll('.case-study-card, .animate-fade-in-up, .hero-section h1, .hero-section p');
            animatedElements.forEach(el => observer.observe(el));
        }
        
        // Add loading states for better UX
        const caseStudyLinks = document.querySelectorAll('.case-study-card a[href]');
        caseStudyLinks.forEach(link => {
            link.addEventListener('click', function(e) {
                const card = this.closest('.case-study-card');
                if (card) {
                    card.classList.add('loading');
                }
            });
        });
        
        // Handle pagination clicks
        const paginationLinks = document.querySelectorAll('.pagination-wrapper a');
        paginationLinks.forEach(link => {
            link.addEventListener('click', function(e) {
                // Add loading state to the page
                document.body.style.opacity = '0.8';
                document.body.style.pointerEvents = 'none';
            });
        });
        
        // Smooth scroll to top when pagination is used
        const paginationWrapper = document.querySelector('.pagination-wrapper');
        if (paginationWrapper) {
            paginationWrapper.addEventListener('click', function(e) {
                if (e.target.tagName === 'A') {
                    setTimeout(() => {
                        window.scrollTo({
                            top: 0,
                            behavior: 'smooth'
                        });
                    }, 100);
                }
            });
        }
    }

    /**
     * Initialize Case Study Title Animations
     */
    function initCaseStudyTitleAnimations() {
        // Check if we're on a case study page
        const isCaseStudyPage = document.body.classList.contains('post-type-case_studies') || 
                               document.querySelector('.case-study-title') !== null;
        
        if (!isCaseStudyPage) return;
        
        // Initialize GSAP animations if available
        if (typeof gsap !== 'undefined' && typeof ScrollTrigger !== 'undefined') {
            gsap.registerPlugin(ScrollTrigger);
            
            // Get all case study titles
            const caseStudyTitles = document.querySelectorAll('.case-study-title');
            if (caseStudyTitles.length === 0) return;
            
            // Function to split text into words
            function splitTextIntoWords(element) {
                const text = element.textContent;
                const words = text.split(/(\s+)/); // Split by whitespace but keep the whitespace
                
                // Clear the element
                element.innerHTML = '';
                
                // Create spans for each word/space
                words.forEach(word => {
                    if (word.trim() === '') {
                        // This is whitespace, add it as is
                        element.appendChild(document.createTextNode(word));
                    } else {
                        // This is a word, wrap it in a span
                        const span = document.createElement('span');
                        span.textContent = word;
                        span.style.display = 'inline-block';
                        span.style.overflow = 'hidden';
                        span.style.opacity = '0';
                        span.style.transform = 'translateY(100%)';
                        element.appendChild(span);
                    }
                });
                
                return element.querySelectorAll('span');
            }
            
            // Function to set up line animations
            function setupLineAnimations(titleElement) {
                const lineLeft = titleElement.parentElement.querySelector('.case-study-line-left');
                const lineRight = titleElement.parentElement.querySelector('.case-study-line-right');
                
                if (lineLeft) {
                    gsap.set(lineLeft, {
                        scaleX: 0,
                        transformOrigin: 'right center'
                    });
                }
                
                if (lineRight) {
                    gsap.set(lineRight, {
                        scaleX: 0,
                        transformOrigin: 'left center'
                    });
                }
                
                return { lineLeft, lineRight };
            }
            
            // Animate each title
            caseStudyTitles.forEach((title, index) => {
                // Split text into words
                const wordSpans = splitTextIntoWords(title);
                if (wordSpans.length === 0) return;
                
                // Set up line animations
                const { lineLeft, lineRight } = setupLineAnimations(title);
                
                // Set initial state for words
                gsap.set(wordSpans, {
                    opacity: 0,
                    y: '100%'
                });
                
                // Create animation timeline
                const tl = gsap.timeline({
                    scrollTrigger: {
                        trigger: title,
                        start: "top 80%",
                        end: "bottom 20%",
                        toggleActions: "play none none reverse"
                    }
                });
                
                // Animate words with reveal effect
                tl.to(wordSpans, {
                    opacity: 1,
                    y: '0%',
                    duration: 0.8,
                    stagger: 0.1,
                    ease: "power2.out"
                });
                
                // Animate lines 0.2s after title animation completes
                if (lineLeft) {
                    tl.to(lineLeft, {
                        scaleX: 1,
                        duration: 0.6,
                        ease: "power2.out"
                    }, "-=0.2"); // Start 0.2s before title animation ends
                }
                
                if (lineRight) {
                    tl.to(lineRight, {
                        scaleX: 1,
                        duration: 0.6,
                        ease: "power2.out"
                    }, "-=0.2"); // Start 0.2s before title animation ends
                }
            });
            
            console.log('Case study title animations initialized');
        } else {
            console.warn('GSAP or ScrollTrigger not available - case study title animations disabled');
            
            // Fallback using Intersection Observer
            const observerOptions = {
                threshold: 0.1,
                rootMargin: '0px 0px -50px 0px'
            };
            
            const observer = new IntersectionObserver((entries) => {
                entries.forEach(entry => {
                    if (entry.isIntersecting) {
                        entry.target.classList.add('animated');
                        observer.unobserve(entry.target);
                    }
                });
            }, observerOptions);
            
            // Observe all case study titles for fallback animation
            caseStudyTitles.forEach(title => observer.observe(title));
        }
    }

    /**
     * Initialize Case Study Section Reveal Animations
     */
    function initCaseStudySectionRevealAnimations() {
        // Check if we're on a case study page
        const isCaseStudyPage = document.body.classList.contains('post-type-case_studies') || 
                               document.querySelector('.case-study-section') !== null;
        
        if (!isCaseStudyPage) return;
        
        // Initialize GSAP animations if available
        if (typeof gsap !== 'undefined' && typeof ScrollTrigger !== 'undefined') {
            gsap.registerPlugin(ScrollTrigger);
            
            // Get all case study sections
            const caseStudySections = document.querySelectorAll('.case-study-section');
            if (caseStudySections.length === 0) return;
            
            // Function to split text into words for description animation
            function splitTextIntoWords(element) {
                const text = element.textContent;
                const words = text.split(/(\s+)/); // Split by whitespace but keep the whitespace
                
                // Clear the element
                element.innerHTML = '';
                
                // Create spans for each word/space
                words.forEach(word => {
                    if (word.trim() === '') {
                        // This is whitespace, add it as is
                        element.appendChild(document.createTextNode(word));
                    } else {
                        // This is a word, wrap it in a span
                        const span = document.createElement('span');
                        span.textContent = word;
                        span.style.display = 'inline-block';
                        span.style.overflow = 'hidden';
                        span.style.opacity = '0';
                        span.style.transform = 'translateY(30px) scale(0.8)';
                        element.appendChild(span);
                    }
                });
                
                return element.querySelectorAll('span');
            }
            
            // Animate each section
            caseStudySections.forEach((section, sectionIndex) => {
                const sectionType = section.getAttribute('data-section');
                const title = section.querySelector('.case-study-title');
                const description = section.querySelector('.case-study-description');
                const image = section.querySelector('.case-study-image');
                
                // Set initial state for images
                if (image) {
                    gsap.set(image, {
                        opacity: 0,
                        scale: 0.8,
                        y: 50
                    });
                }
                
                // Set initial state for descriptions
                if (description) {
                    // Split text into words for rotation animation
                    const wordSpans = splitTextIntoWords(description);
                    
                    // Set initial state for words
                    gsap.set(wordSpans, {
                        opacity: 0,
                        y: 30,
                        scale: 0.8
                    });
                }
                
                // Create animation timeline for this section
                const sectionTl = gsap.timeline({
                    scrollTrigger: {
                        trigger: section,
                        start: "top 80%",
                        end: "bottom 20%",
                        toggleActions: "play none none reverse"
                    }
                });
                
                // Animate image with zoom effect
                if (image) {
                    sectionTl.to(image, {
                        opacity: 1,
                        scale: 1,
                        y: 0,
                        duration: 1.2,
                        ease: "power2.out"
                    }, 0); // Start immediately
                }
                
                // Animate description words with fast slide-up + scale effect
                if (description) {
                    const wordSpans = description.querySelectorAll('span');
                    sectionTl.to(wordSpans, {
                        opacity: 1,
                        y: 0,
                        scale: 1,
                        duration: 0.6,
                        stagger: 0.03, // Much faster stagger
                        ease: "back.out(1.2)" // Bouncy easing for modern feel
                    }, 0.2); // Start 0.2s after image animation begins
                }
                
                // Add stagger delay between sections
                if (sectionIndex > 0) {
                    sectionTl.delay(sectionIndex * 0.2);
                }
            });
            
            console.log('Case study section reveal animations initialized');
        } else {
            console.warn('GSAP or ScrollTrigger not available - case study section reveal animations disabled');
            
            // Fallback using Intersection Observer
            const observerOptions = {
                threshold: 0.1,
                rootMargin: '0px 0px -50px 0px'
            };
            
            const observer = new IntersectionObserver((entries) => {
                entries.forEach(entry => {
                    if (entry.isIntersecting) {
                        entry.target.classList.add('animated');
                        observer.unobserve(entry.target);
                    }
                });
            }, observerOptions);
            
            // Observe all case study sections for fallback animation
            caseStudySections.forEach(section => observer.observe(section));
        }
    }

    /**
     * Initialize Origin Story Animations
     */
    function initOriginStoryAnimations() {
        // Check if we're on the about page
        const isAboutPage = document.body.classList.contains('page-template-page-about') || 
                           document.querySelector('.origin-story-section') !== null;
        
        if (!isAboutPage) return;
        
        // Initialize GSAP animations if available
        if (typeof gsap !== 'undefined' && typeof ScrollTrigger !== 'undefined') {
            gsap.registerPlugin(ScrollTrigger);
            
            // Get the origin story section
            const originStorySection = document.querySelector('.origin-story-section');
            if (!originStorySection) return;
            
            // Get all paragraph elements in the origin story section
            const paragraphs = originStorySection.querySelectorAll('.paragraph');
            if (paragraphs.length === 0) return;
            
            // Split each paragraph into words and wrap them in spans
            paragraphs.forEach((paragraph, paragraphIndex) => {
                const text = paragraph.textContent;
                const words = text.split(' ');
                
                // Clear the paragraph and add words as individual spans
                paragraph.innerHTML = '';
                words.forEach((word, wordIndex) => {
                    const wordSpan = document.createElement('span');
                    wordSpan.textContent = word;
                    wordSpan.style.display = 'inline-block';
                    wordSpan.style.overflow = 'hidden';
                    wordSpan.style.opacity = '0';
                    wordSpan.style.transform = 'translateY(100%)';
                    wordSpan.style.marginRight = '0.25em'; // Add proper spacing
                    paragraph.appendChild(wordSpan);
                });
            });
            
            // Create animation timeline
            const tl = gsap.timeline({
                scrollTrigger: {
                    trigger: originStorySection,
                    start: "top 80%",
                    end: "bottom 20%",
                    toggleActions: "play none none reverse"
                }
            });
            
            // Animate each paragraph's words
            paragraphs.forEach((paragraph, paragraphIndex) => {
                const wordSpans = paragraph.querySelectorAll('span');
                
                // Set initial state for all words in this paragraph
                gsap.set(wordSpans, {
                    opacity: 0,
                    y: '100%'
                });
                
                // Add words animation to timeline with stagger
                tl.to(wordSpans, {
                    opacity: 1,
                    y: '0%',
                    duration: 0.6,
                    ease: "power2.out",
                    stagger: {
                        amount: 0.8,
                        from: "start"
                    }
                }, paragraphIndex * 0.3); // Delay between paragraphs
            });
            
            console.log('Origin story word animations initialized');
        } else {
            console.warn('GSAP or ScrollTrigger not available - origin story animations disabled');
        }
    }

    /**
     * Initialize Company Evolution Section Animations
     */
    function initCompanyEvolutionAnimations() {
        // Check if we're on the about page and company evolution section exists
        const isAboutPage = document.body.classList.contains('page-template-page-about') || 
                           document.querySelector('.company-evolution-section') !== null;
        
        if (!isAboutPage) return;
        
        // Initialize GSAP animations if available
        if (typeof gsap !== 'undefined' && typeof ScrollTrigger !== 'undefined') {
            gsap.registerPlugin(ScrollTrigger);
            
            // Get the company evolution section
            const companyEvolutionSection = document.querySelector('.company-evolution-section');
            if (!companyEvolutionSection) return;
            
            // Get animation elements
            const evolutionCard = companyEvolutionSection.querySelector('.company-evolution-card');
            const evolutionLogo = companyEvolutionSection.querySelector('.company-evolution-logo');
            const evolutionParagraphs = companyEvolutionSection.querySelectorAll('.company-evolution-paragraph');
            
            if (!evolutionCard) return;
            
            // Set initial state for all elements
            gsap.set(evolutionCard, {
                opacity: 0,
                scale: 0.8,
                y: 60
            });
            
            if (evolutionLogo) {
                gsap.set(evolutionLogo, {
                    opacity: 0,
                    scale: 0.9,
                    y: 30
                });
            }
            
            evolutionParagraphs.forEach(paragraph => {
                gsap.set(paragraph, {
                    opacity: 0,
                    y: 40
                });
            });
            
            // Create main animation timeline
            const tl = gsap.timeline({
                scrollTrigger: {
                    trigger: companyEvolutionSection,
                    start: "top 75%",
                    end: "bottom 25%",
                    toggleActions: "play none none reverse"
                }
            });
            
            // Animate the card with zoom effect
            tl.to(evolutionCard, {
                opacity: 1,
                scale: 1,
                y: 0,
                duration: 1.2,
                ease: "power2.out"
            });
            
            // Animate logo with slight delay
            if (evolutionLogo) {
                tl.to(evolutionLogo, {
                    opacity: 1,
                    scale: 1,
                    y: 0,
                    duration: 0.8,
                    ease: "power2.out"
                }, "-=0.6");
            }
            
            // Animate paragraphs with stagger
            if (evolutionParagraphs.length > 0) {
                tl.to(evolutionParagraphs, {
                    opacity: 1,
                    y: 0,
                    duration: 0.8,
                    ease: "power2.out",
                    stagger: {
                        amount: 0.6,
                        from: "start"
                    }
                }, "-=0.4");
            }
            
            console.log('Company evolution animations initialized');
        } else {
            console.warn('GSAP or ScrollTrigger not available - company evolution animations disabled');
            
            // Fallback using Intersection Observer
            const observerOptions = {
                threshold: 0.1,
                rootMargin: '0px 0px -50px 0px'
            };
            
            const observer = new IntersectionObserver((entries) => {
                entries.forEach(entry => {
                    if (entry.isIntersecting) {
                        entry.target.classList.add('animated');
                        observer.unobserve(entry.target);
                    }
                });
            }, observerOptions);
            
            // Observe the card for fallback animation
            observer.observe(evolutionCard);
        }
    }

    /**
     * Initialize Leadership Section Animations
     */
    function initLeadershipAnimations() {
        // Check if we're on the about page and leadership section exists
        const isAboutPage = document.body.classList.contains('page-template-page-about') || 
                           document.querySelector('.leadership-section') !== null;
        
        if (!isAboutPage) return;
        
        // Initialize GSAP animations if available
        if (typeof gsap !== 'undefined' && typeof ScrollTrigger !== 'undefined') {
            gsap.registerPlugin(ScrollTrigger);
            
            // Get the leadership section
            const leadershipSection = document.querySelector('.leadership-section');
            if (!leadershipSection) return;
            
            // Get all leadership members
            const leadershipMembers = leadershipSection.querySelectorAll('.leadership-member');
            if (leadershipMembers.length === 0) return;
            
            // Set initial state for all elements
            leadershipMembers.forEach((member, memberIndex) => {
                const textContent = member.querySelector('.leadership-text-content');
                const imageContent = member.querySelector('.leadership-image-content');
                const direction = textContent?.getAttribute('data-animation-direction');
                
                // Set initial state for text content
                if (textContent) {
                    const xOffset = direction === 'left' ? -100 : 100;
                    gsap.set(textContent, {
                        opacity: 0,
                        x: xOffset,
                        y: 30
                    });
                }
                
                // Set initial state for image content
                if (imageContent) {
                    gsap.set(imageContent, {
                        opacity: 0,
                        y: 80,
                        scale: 0.9
                    });
                }
            });
            
            // Create animation for each member
            leadershipMembers.forEach((member, memberIndex) => {
                const textContent = member.querySelector('.leadership-text-content');
                const imageContent = member.querySelector('.leadership-image-content');
                const direction = textContent?.getAttribute('data-animation-direction');
                
                // Create timeline for this member
                const memberTl = gsap.timeline({
                    scrollTrigger: {
                        trigger: member,
                        start: "top 80%",
                        end: "bottom 20%",
                        toggleActions: "play none none reverse"
                    }
                });
                
                // Animate text content with direction-based slide
                if (textContent) {
                    const xOffset = direction === 'left' ? -100 : 100;
                    memberTl.to(textContent, {
                        opacity: 1,
                        x: 0,
                        y: 0,
                        duration: 0.8,
                        ease: "power2.out"
                    });
                }
                
                // Animate image content with slide up
                if (imageContent) {
                    memberTl.to(imageContent, {
                        opacity: 1,
                        y: 0,
                        scale: 1,
                        duration: 0.8,
                        ease: "power2.out"
                    }, "-=0.4"); // Start slightly before text animation ends
                }
                
                // Add stagger delay between members
                if (memberIndex > 0) {
                    memberTl.delay(memberIndex * 0.2);
                }
            });
            
            console.log('Leadership section animations initialized');
        } else {
            console.warn('GSAP or ScrollTrigger not available - leadership animations disabled');
            
            // Fallback using Intersection Observer
            const observerOptions = {
                threshold: 0.1,
                rootMargin: '0px 0px -50px 0px'
            };
            
            const observer = new IntersectionObserver((entries) => {
                entries.forEach(entry => {
                    if (entry.isIntersecting) {
                        entry.target.classList.add('animated');
                        observer.unobserve(entry.target);
                    }
                });
            }, observerOptions);
            
            // Observe all leadership members for fallback animation
            leadershipMembers.forEach(member => observer.observe(member));
        }
    }

    /**
     * Initialize Portfolio Animations
     */
    function initPortfolioAnimations() {
        // Check if we're on a portfolio page
        const isPortfolioPage = document.body.classList.contains('post-type-portfolio') || 
                               document.body.classList.contains('archive-portfolio') ||
                               document.querySelector('.portfolio-item') !== null;
        
        if (!isPortfolioPage) return;
        
        // Get all portfolio items
        const portfolioItems = document.querySelectorAll('.portfolio-item');
        if (portfolioItems.length === 0) return;
        
        // Set initial state for all items
        portfolioItems.forEach(item => {
            item.style.opacity = '0';
            item.style.transform = 'translateY(60px)';
            item.style.transition = 'all 0.8s ease-out';
        });
        
        // Check for items already visible on page load
        const checkInitialVisibility = () => {
            portfolioItems.forEach((item, index) => {
                const rect = item.getBoundingClientRect();
                const isVisible = rect.top < window.innerHeight && rect.bottom > 0;
                
                if (isVisible) {
                    // Animate immediately with staggered delay
                    setTimeout(() => {
                        item.style.opacity = '1';
                        item.style.transform = 'translateY(0)';
                    }, index * 200);
                }
            });
        };
        
        // Check initial visibility
        checkInitialVisibility();
        
        // Use CSS animations with Intersection Observer for better grid compatibility
        const observerOptions = {
            threshold: 0.1,
            rootMargin: '0px 0px -50px 0px'
        };
        
        const observer = new IntersectionObserver((entries) => {
            entries.forEach(entry => {
                if (entry.isIntersecting) {
                    const item = entry.target;
                    const index = Array.from(portfolioItems).indexOf(item);
                    
                    // Add staggered delay and animate
                    setTimeout(() => {
                        item.style.opacity = '1';
                        item.style.transform = 'translateY(0)';
                    }, index * 200); // 200ms = 0.2s delay
                    
                    observer.unobserve(item);
                }
            });
        }, observerOptions);
        
        // Observe all portfolio items
        portfolioItems.forEach(item => {
            observer.observe(item);
        });
        
        console.log('Portfolio animations initialized with CSS + Intersection Observer');
    }

    /**
     * Team Section Word Animation
     */
    function initTeamSectionWordAnimation() {
        const teamDescription = document.querySelector('.our-team-section-description');
        
        if (!teamDescription) {
            console.log('Team section description not found');
            return;
        }

        // Check if GSAP and ScrollTrigger are available
        if (typeof gsap !== 'undefined' && typeof ScrollTrigger !== 'undefined') {
            gsap.registerPlugin(ScrollTrigger);
            
            // Function to split text into words while preserving spaces
            function splitTextIntoWords(element) {
                const text = element.textContent;
                const words = text.split(/(\s+)/); // Split by whitespace but keep the whitespace
                
                // Clear the element
                element.innerHTML = '';
                
                // Create spans for each word/space
                words.forEach(word => {
                    if (word.trim() === '') {
                        // This is whitespace, add it as is
                        element.appendChild(document.createTextNode(word));
                    } else {
                        // This is a word, wrap it in a span
                        const span = document.createElement('span');
                        span.textContent = word;
                        span.style.display = 'inline-block';
                        span.style.opacity = '0';
                        span.style.transform = 'translateY(30px)';
                        element.appendChild(span);
                    }
                });
                
                return element.querySelectorAll('span');
            }

            // Split text into words
            const wordSpans = splitTextIntoWords(teamDescription);
            
            if (wordSpans.length === 0) {
                console.log('No words found in team description');
                return;
            }

            // Set initial state
            gsap.set(wordSpans, {
                opacity: 0,
                y: 30,
                rotationX: 90
            });

            // Create animation timeline
            const tl = gsap.timeline({
                scrollTrigger: {
                    trigger: teamDescription,
                    start: 'top 80%',
                    end: 'bottom 20%',
                    toggleActions: 'play none none reverse',
                    once: true
                }
            });

            // Animate words one by one
            tl.to(wordSpans, {
                opacity: 1,
                y: 0,
                rotationX: 0,
                duration: 0.6,
                stagger: 0.08,
                ease: 'power2.out'
            });

            console.log('Team section word animation initialized');
        } else {
            console.warn('GSAP or ScrollTrigger not available - team section word animation disabled');
            
            // Fallback using Intersection Observer
            const observerOptions = {
                threshold: 0.1,
                rootMargin: '0px 0px -50px 0px'
            };
            
            const observer = new IntersectionObserver((entries) => {
                entries.forEach(entry => {
                    if (entry.isIntersecting) {
                        entry.target.classList.add('animated');
                        observer.unobserve(entry.target);
                    }
                });
            }, observerOptions);
            
            observer.observe(teamDescription);
        }
    }

    /**
     * Initialize CTA Section Animations
     */
    function initCTAAnimations() {
        // Check if CTA section exists
        const ctaSection = document.querySelector('.cta-section');
        if (!ctaSection) return;

        // Initialize GSAP animations if available
        if (typeof gsap !== 'undefined' && typeof ScrollTrigger !== 'undefined') {
            gsap.registerPlugin(ScrollTrigger);
            
            // Get CTA elements
            const ctaTitle = ctaSection.querySelector('.cta-title');
            const ctaDescription = ctaSection.querySelector('.cta-description');
            const ctaButton = ctaSection.querySelector('.cta-button');
            
            if (!ctaTitle) return;

            // Function to split text into words for title animation
            function splitTextIntoWords(element) {
                const text = element.textContent;
                const words = text.split(/(\s+)/); // Split by whitespace but keep the whitespace
                
                // Clear the element
                element.innerHTML = '';
                
                // Create spans for each word/space
                words.forEach(word => {
                    if (word.trim() === '') {
                        // This is whitespace, add it as is
                        element.appendChild(document.createTextNode(word));
                    } else {
                        // This is a word, wrap it in a span
                        const span = document.createElement('span');
                        span.textContent = word;
                        span.style.display = 'inline-block';
                        span.style.overflow = 'hidden';
                        span.style.opacity = '0';
                        span.style.transform = 'translateY(100%)';
                        element.appendChild(span);
                    }
                });
                
                return element.querySelectorAll('span');
            }

            // Split title text into words
            const titleWordSpans = splitTextIntoWords(ctaTitle);
            
            if (titleWordSpans.length === 0) {
                console.log('No words found in CTA title');
                return;
            }

            // Set initial state for all elements
            gsap.set(titleWordSpans, {
                opacity: 0,
                y: '100%'
            });

            if (ctaDescription) {
                gsap.set(ctaDescription, {
                    opacity: 0,
                    y: 40
                });
            }

            if (ctaButton) {
                gsap.set(ctaButton, {
                    opacity: 0,
                    y: 30
                });
            }

            // Create main animation timeline
            const tl = gsap.timeline({
                scrollTrigger: {
                    trigger: ctaSection,
                    start: "top 80%",
                    end: "bottom 20%",
                    toggleActions: "play none none reverse"
                }
            });

            // Animate title words with reveal effect
            tl.to(titleWordSpans, {
                opacity: 1,
                y: '0%',
                duration: 0.8,
                stagger: 0.08,
                ease: "power2.out"
            });

            // Animate description with fade-in-up after 0.4s delay
            if (ctaDescription) {
                tl.to(ctaDescription, {
                    opacity: 1,
                    y: 0,
                    duration: 0.8,
                    ease: "power2.out"
                }, "-=0.4"); // Start 0.4s before title animation ends
            }

            // Animate button with slight delay after description
            if (ctaButton) {
                tl.to(ctaButton, {
                    opacity: 1,
                    y: 0,
                    duration: 0.6,
                    ease: "power2.out"
                }, "-=0.2"); // Start 0.2s before description animation ends
            }

            console.log('CTA section animations initialized');
        } else {
            console.warn('GSAP or ScrollTrigger not available - CTA animations disabled');
            
            // Fallback using Intersection Observer
            const observerOptions = {
                threshold: 0.1,
                rootMargin: '0px 0px -50px 0px'
            };
            
            const observer = new IntersectionObserver((entries) => {
                entries.forEach(entry => {
                    if (entry.isIntersecting) {
                        entry.target.classList.add('animated');
                        observer.unobserve(entry.target);
                    }
                });
            }, observerOptions);
            
            // Observe CTA elements for fallback animation
            const ctaElements = ctaSection.querySelectorAll('.cta-title, .cta-description, .cta-button');
            ctaElements.forEach(el => observer.observe(el));
        }
    }

    /**
     * Scroll Performance Monitor
     */
    function initScrollPerformanceMonitor() {
        let scrollStartTime = 0;
        let scrollEndTime = 0;
        let frameCount = 0;
        
        function onScrollStart() {
            scrollStartTime = performance.now();
            frameCount = 0;
        }
        
        function onScrollEnd() {
            scrollEndTime = performance.now();
            const scrollDuration = scrollEndTime - scrollStartTime;
            const fps = frameCount / (scrollDuration / 1000);
            
            // Log performance issues
            if (fps < 30) {
                console.warn('Scroll performance issue detected:', {
                    fps: fps.toFixed(2),
                    duration: scrollDuration.toFixed(2) + 'ms',
                    frames: frameCount
                });
            }
        }
        
        let scrollTimeout;
        window.addEventListener('scroll', () => {
            frameCount++;
            
            if (scrollStartTime === 0) {
                onScrollStart();
            }
            
            clearTimeout(scrollTimeout);
            scrollTimeout = setTimeout(() => {
                onScrollEnd();
                scrollStartTime = 0;
            }, 150);
        }, { passive: true });
    }

    // Expose functions globally for potential use
    window.SraviaTheme = {
        initAnimations,
        initMobileMenu,
        initSmoothScrolling,
        initSectionTitleDots,
        initCaseStudiesAnimations,
        initCaseStudiesCarousel,
        initCaseStudyTitleAnimations,
        initCaseStudySectionRevealAnimations,
        initOriginStoryAnimations,
        initCompanyEvolutionAnimations,
        initLeadershipAnimations,
        initTeamSectionWordAnimation,
        initCTAAnimations,
        optimizeScrollPerformance,
        
        // Enhanced smooth scrolling functions
        smoothScrollToElement,
        smoothScrollFallback,
        initScrollToTopButton,
        initFormSmoothScroll,
        initPaginationSmoothScroll,
        initPageNavigationSmoothScroll,
        
        // Utility function to control circular dots animation
        controlCircularDotsAnimation: function(speed = 'normal') {
            const dotsSvgs = document.querySelectorAll('.animate-circular-dots');
            const speeds = {
                'slow': '30s',
                'normal': '20s',
                'fast': '15s',
                'very-fast': '10s'
            };
            
            dotsSvgs.forEach(svg => {
                if (speeds[speed]) {
                    svg.style.animationDuration = speeds[speed];
                }
            });
        },
        
        // Performance monitoring
        enableScrollMonitoring: initScrollPerformanceMonitor
    };

})();

 